import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Badge } from "react-bootstrap";
import moment from "moment";
import {
  FaEdit,
  FaTrash,
  FaTrashRestore,
  FaTrashAlt,
  FaFlag,
  FaTasks,
  FaCalendarAlt,
  FaUser,
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaPercentage,
  FaInfoCircle,
  FaClock,
} from "react-icons/fa";
import { MdOutlineTrackChanges } from "react-icons/md";
import { TfiViewListAlt } from "react-icons/tfi";
import { toast } from "react-toastify";
import "./ProjectMilestoneTab.css";

const ProjectMilestoneTab = ({
  // Data
  milestones,
  milestonesLoading,
  milestoneTasks,
  expandedMilestones,
  taskAssignedUsers,

  // Permissions
  hasProjectAdminRole,
  hasProjectManagerRole,
  hasEditPermission,

  // Handlers
  handleCreateMilestone,
  handleEditMilestone,
  toggleMilestoneExpand,
  handleTaskNameClick,
  handleEditTask,
  formatDate,
  getStatusBadge,
  getPriorityBadge,
  refreshMilestones,

  // Delete/Restore handlers
  onSoftDeleteMilestone,
  onRestoreMilestone,
  onHardDeleteMilestone,

  showInactiveMilestones,
  setShowInactiveMilestones,

  // IMPORTANT: Add these props for task status resolution
  getTaskStatusName, // Function to convert task status GUID to name
  milestoneTypesMap = {},
  milestoneStatusesMap = {},
}) => {
  const canEdit = hasProjectAdminRole() || hasProjectManagerRole();

  // Local state for delete/restore operations
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Helper function to resolve type GUID to name
  const getTypeDisplayName = (typeGuid) => {
    if (!typeGuid) return "Not set";
    if (typeof typeGuid === "string" && !typeGuid.includes("-")) {
      return typeGuid;
    }
    const typeGuidLower = typeGuid.toLowerCase();
    return milestoneTypesMap[typeGuidLower] || typeGuid.substring(0, 8);
  };

  // Helper function to resolve status GUID to name
  const getStatusDisplayName = (statusGuid) => {
    if (!statusGuid) return "Not Started";
    if (typeof statusGuid === "string" && !statusGuid.includes("-")) {
      return statusGuid;
    }
    const statusGuidLower = statusGuid.toLowerCase();
    return milestoneStatusesMap[statusGuidLower] || statusGuid.substring(0, 8);
  };

  // Helper function to get milestone status badge class
  const getMilestoneStatusBadgeClass = (statusName) => {
    const statusMap = {
      Completed: "ptm-status-completed",
      "In Progress": "ptm-status-progress",
      "On Hold": "ptm-status-hold",
      Cancelled: "ptm-status-cancelled",
      Delayed: "ptm-status-delayed",
      "Not Started": "ptm-status-notstarted",
    };
    return statusMap[statusName] || "ptm-status-default";
  };

  // Helper function to get task status class based on status name
  const getTaskStatusClass = (statusName) => {
    const statusClassMap = {
      Completed: "ptm-task-completed",
      "In Progress": "ptm-task-progress",
      Open: "ptm-task-open",
      Closed: "ptm-task-closed",
      Pending: "ptm-task-pending",
      Approved: "ptm-task-approved",
      Rejected: "ptm-task-rejected",
      "Not Started": "ptm-task-notstarted",
      "On Hold": "ptm-task-hold",
      Cancelled: "ptm-task-cancelled",
    };
    return statusClassMap[statusName] || "ptm-task-default";
  };

  // Helper function to get priority class
  const getPriorityClass = (priority) => {
    if (!priority) return "ptm-priority-default";
    const priorityLower = priority.toLowerCase();
    if (priorityLower === "high" || priorityLower === "critical")
      return "ptm-priority-high";
    if (priorityLower === "medium") return "ptm-priority-medium";
    if (priorityLower === "low") return "ptm-priority-low";
    return "ptm-priority-default";
  };

  // Format hours for display
  const formatHours = (hours) => {
    if (hours === undefined || hours === null) return "Not set";
    if (hours === 0) return "0 hrs";
    if (hours % 1 === 0) {
      return `${hours} hr${hours !== 1 ? "s" : ""}`;
    }
    return `${hours} hrs`;
  };

  // Calculate statistics
  const activeMilestonesCount = milestones.filter(
    (m) => m.isActive !== false && m.active !== false,
  ).length;
  const inactiveMilestonesCount = milestones.filter(
    (m) => m.isActive === false || m.active === false,
  ).length;

  const getTotalProgress = () => {
    const activeMilestones = milestones.filter(
      (m) => m.isActive !== false && m.active !== false,
    );
    if (activeMilestones.length === 0) return 0;
    const totalProgress = activeMilestones.reduce((sum, milestone) => {
      return (
        sum + (milestone.calculatedPercentage || milestone.percentage || 0)
      );
    }, 0);
    return Math.round(totalProgress / activeMilestones.length);
  };

  const getProgressColor = (percent) => {
    if (percent >= 80) return "#10b981";
    if (percent >= 50) return "#f59e0b";
    if (percent >= 25) return "#f97316";
    return "#ef4444";
  };

  // Soft delete handler
  const handleSoftDelete = async (milestone) => {
    if (!onSoftDeleteMilestone) {
      toast.error("Soft delete function not available");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to deactivate "${milestone.name}"?\n\nDeactivated milestones will be hidden but can be restored later.`,
      )
    ) {
      return;
    }

    setProcessing(true);
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const result = await onSoftDeleteMilestone(
        milestone.milestoneMavroId,
        String(userDetails?.userId),
      );

      if (result?.message || result?.Message) {
        toast.success(
          result.message ||
            result.Message ||
            "Milestone deactivated successfully",
        );
      } else {
        toast.success("Milestone deactivated successfully");
      }

      if (refreshMilestones) refreshMilestones();
    } catch (error) {
      console.error("Error deactivating milestone:", error);
      toast.error(error.message || "Failed to deactivate milestone");
    } finally {
      setProcessing(false);
    }
  };

  // Restore handler
  const handleRestore = async (milestone) => {
    if (!onRestoreMilestone) {
      toast.error("Restore function not available");
      return;
    }

    if (
      !window.confirm(`Are you sure you want to restore "${milestone.name}"?`)
    ) {
      return;
    }

    setProcessing(true);
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const result = await onRestoreMilestone(
        milestone.milestoneMavroId,
        String(userDetails?.userId),
      );

      if (result?.message || result?.Message) {
        toast.success(
          result.message || result.Message || "Milestone restored successfully",
        );
      } else {
        toast.success("Milestone restored successfully");
      }

      if (refreshMilestones) refreshMilestones();
    } catch (error) {
      console.error("Error restoring milestone:", error);
      toast.error(error.message || "Failed to restore milestone");
    } finally {
      setProcessing(false);
    }
  };

  // Permanent delete handler
  const handlePermanentDelete = async (milestone) => {
    if (!onHardDeleteMilestone) {
      toast.error("Permanent delete function not available");
      return;
    }

    if (
      !window.confirm(
        `⚠️ WARNING: This action is irreversible!\n\nAre you sure you want to permanently delete "${milestone.name}"?\n\nThis will permanently remove the milestone and cannot be undone.`,
      )
    ) {
      return;
    }

    setProcessing(true);
    try {
      const result = await onHardDeleteMilestone(milestone.milestoneMavroId);

      if (result?.message || result?.Message) {
        toast.warning(
          result.message || result.Message || "Milestone permanently deleted",
        );
      } else {
        toast.warning("Milestone permanently deleted");
      }

      if (refreshMilestones) refreshMilestones();
    } catch (error) {
      console.error("Error deleting milestone:", error);
      toast.error(error.message || "Failed to delete milestone");
    } finally {
      setProcessing(false);
    }
  };

  // Filter milestones based on showInactiveMilestones
  const displayedMilestones = showInactiveMilestones
    ? milestones
    : milestones.filter((m) => m.isActive !== false && m.active !== false);

  return (
    <div className="ptm-container">
      {/* Header Section */}
      <div className="ptm-header">
        <div className="ptm-header-left">
          <div className="ptm-stats">
            <div className="ptm-stat-item">
              <span className="ptm-stat-value">{milestones.length}</span>
              <span className="ptm-stat-label">Total Milestones</span>
            </div>
            <div className="ptm-stat-divider"></div>
            <div className="ptm-stat-item">
              <span className="ptm-stat-value ptm-active">
                {activeMilestonesCount}
              </span>
              <span className="ptm-stat-label">Active</span>
            </div>
            <div className="ptm-stat-divider"></div>
            <div className="ptm-stat-item">
              <span className="ptm-stat-value ptm-inactive">
                {inactiveMilestonesCount}
              </span>
              <span className="ptm-stat-label">Inactive</span>
            </div>
          </div>
          <div className="ptm-progress-ring">
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="22"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="3"
              />
              <circle
                cx="25"
                cy="25"
                r="22"
                fill="none"
                stroke={getProgressColor(getTotalProgress())}
                strokeWidth="3"
                strokeDasharray={`${getTotalProgress() * 1.38} 138`}
                strokeLinecap="round"
                transform="rotate(-90 25 25)"
              />
            </svg>
            <span className="ptm-progress-text">{getTotalProgress()}%</span>
          </div>
        </div>

        <div className="ptm-header-right">
          <div className="ptm-inactive-switch">
            <Form.Check
              type="switch"
              id="show-inactive-milestones-switch"
              label="Show Inactive"
              checked={showInactiveMilestones}
              onChange={(e) => setShowInactiveMilestones(e.target.checked)}
              className="ptm-switch"
            />
          </div>

          {canEdit && !showInactiveMilestones && (
            <button
              className="ptm-btn-primary"
              onClick={handleCreateMilestone}
              disabled={processing}
            >
              <FaPlus /> Add Milestone
            </button>
          )}
        </div>
      </div>

      {/* Content Section */}
      {milestonesLoading ? (
        <div className="ptm-loading">
          <div className="ptm-spinner"></div>
          <p>Loading milestones...</p>
        </div>
      ) : displayedMilestones.length === 0 ? (
        <div className="ptm-empty">
          <div className="ptm-empty-icon">
            <FaFlag />
          </div>
          <h3>No Milestones Found</h3>
          <p>
            {showInactiveMilestones
              ? "No inactive milestones found."
              : "Create your first milestone to track project progress."}
          </p>
          {canEdit && !showInactiveMilestones && (
            <button className="ptm-btn-primary" onClick={handleCreateMilestone}>
              <FaPlus /> Create Milestone
            </button>
          )}
        </div>
      ) : (
        <div className="ptm-grid">
          {displayedMilestones.map((milestone) => {
            const isActive =
              milestone.isActive !== false && milestone.active !== false;
            const progress =
              milestone.calculatedPercentage || milestone.percentage || 0;
            const isExpanded =
              expandedMilestones[milestone.milestoneMavroId] || false;
            const tasks = milestoneTasks[milestone.milestoneMavroId] || [];

            const typeDisplayName = getTypeDisplayName(milestone.Type);
            const statusDisplayName = getStatusDisplayName(milestone.Status);
            const statusBadgeClass =
              getMilestoneStatusBadgeClass(statusDisplayName);
            const hours = milestone.hour;

            return (
              <div
                key={milestone.milestoneMavroId}
                className={`ptm-card ${!isActive ? "inactive" : ""} ${isExpanded ? "expanded" : ""}`}
              >
                {/* Card Header */}
                <div
                  className="ptm-card-header"
                  onClick={() =>
                    toggleMilestoneExpand(milestone.milestoneMavroId)
                  }
                >
                  <div className="ptm-card-header-left">
                    <div className="ptm-card-icon">
                      <FaFlag />
                    </div>
                    <div className="ptm-card-info">
                      <div className="ptm-card-title">
                        <span className="ptm-card-name">{milestone.name}</span>
                        {!isActive && (
                          <Badge bg="secondary" className="ptm-inactive-badge">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <div className="ptm-card-meta">
                        <span className="ptm-card-id">
                          {milestone.milestoneMavroId}
                        </span>
                        {typeDisplayName && typeDisplayName !== "Not set" && (
                          <span className="ptm-card-type">
                            {typeDisplayName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ptm-card-header-right">
                    <div className="ptm-actions">
                      <button
                        className="ptm-action-btn view"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isExpanded)
                            toggleMilestoneExpand(milestone.milestoneMavroId);
                        }}
                        title="View tasks"
                      >
                        <TfiViewListAlt /> Tasks ({tasks.length})
                      </button>

                      {isActive
                        ? canEdit && (
                            <>
                              <button
                                className="ptm-action-btn edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditMilestone(milestone);
                                }}
                                title="Edit milestone"
                              >
                                <FaEdit /> Edit
                              </button>
                              <button
                                className="ptm-action-btn deactivate"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSoftDelete(milestone);
                                }}
                                title="Deactivate milestone (soft delete)"
                                disabled={processing}
                              >
                                <FaTrash /> Deactivate
                              </button>
                            </>
                          )
                        : canEdit && (
                            <>
                              <button
                                className="ptm-action-btn restore"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestore(milestone);
                                }}
                                title="Restore milestone"
                                disabled={processing}
                              >
                                <FaTrashRestore /> Restore
                              </button>
                              <button
                                className="ptm-action-btn hard-delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePermanentDelete(milestone);
                                }}
                                title="Permanently delete milestone"
                                disabled={processing}
                              >
                                <FaTrashAlt /> Delete
                              </button>
                            </>
                          )}
                    </div>

                    <div className="ptm-card-stats">
                      <div className="ptm-stat-chip">
                        <FaTasks />
                        <span>{tasks.length} tasks</span>
                      </div>
                      {hours !== undefined && hours !== null && (
                        <div className="ptm-stat-chip ptm-hours-chip">
                          <FaClock />
                          <span>{formatHours(hours)}</span>
                        </div>
                      )}
                      {isActive && (
                        <div className="ptm-progress-mini">
                          <div className="ptm-progress-bar-mini">
                            <div
                              className="ptm-progress-fill-mini"
                              style={{
                                width: `${progress}%`,
                                backgroundColor: getProgressColor(progress),
                              }}
                            ></div>
                          </div>
                          <span className="ptm-progress-text-mini">
                            {progress}%
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="ptm-card-expand">
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                {(isActive || isExpanded) && (
                  <div className="ptm-card-body">
                    <div className="ptm-status-section">
                      <span
                        className={`ptm-milestone-status ${statusBadgeClass}`}
                      >
                        {statusDisplayName || "Not Started"}
                      </span>
                      {milestone.createdBy &&
                        !milestone.createdBy.includes("-") && (
                          <div className="ptm-created-info">
                            <FaUser />
                            <span>Created by: {milestone.createdBy}</span>
                            {milestone.createdOnDate && (
                              <span className="ptm-created-date">
                                on {formatDate(milestone.createdOnDate)}
                              </span>
                            )}
                          </div>
                        )}
                    </div>

                    {hours !== undefined && hours !== null && hours > 0 && (
                      <div className="ptm-hours-info">
                        <FaClock className="ptm-hours-icon" />
                        <span>Estimated Hours: {formatHours(hours)}</span>
                      </div>
                    )}

                    {milestone.description && (
                      <div className="ptm-description">
                        <FaInfoCircle />
                        <span>{milestone.description}</span>
                      </div>
                    )}

                    <div className="ptm-dates-section">
                      <div className="ptm-date-group">
                        <div className="ptm-date-label">Planned Dates</div>
                        <div className="ptm-dates">
                          <span>
                            <FaCalendarAlt /> Start:{" "}
                            {formatDate(milestone.plannedStartDate)}
                          </span>
                          <span>
                            <FaCalendarAlt /> End:{" "}
                            {formatDate(milestone.plannedEndDate)}
                          </span>
                        </div>
                      </div>
                      {(milestone.actualStartDate ||
                        milestone.actualEndDate) && (
                        <div className="ptm-date-group">
                          <div className="ptm-date-label">Actual Dates</div>
                          <div className="ptm-dates">
                            <span>
                              <FaCalendarAlt /> Start:{" "}
                              {formatDate(milestone.actualStartDate) || "-"}
                            </span>
                            <span>
                              <FaCalendarAlt /> End:{" "}
                              {formatDate(milestone.actualEndDate) || "-"}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {milestone.calculatedPercentage !== undefined &&
                      isActive && (
                        <div className="ptm-progress-details">
                          <div className="ptm-progress-stat">
                            <FaPercentage />
                            <span>
                              Completed: {milestone.completedTasks || 0}/
                              {milestone.totalTasks || 0} tasks
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                )}

                {/* Expanded View - Tasks List with proper status names */}
                {isExpanded && (
                  <div className="ptm-expanded-view">
                    <div className="ptm-expanded-header">
                      <h4>
                        <FaTasks />
                        Tasks in this milestone
                      </h4>
                      <span className="ptm-expanded-count">
                        Total: {tasks.length}
                      </span>
                    </div>

                    {tasks.length === 0 ? (
                      <div className="ptm-items-empty">
                        <FaTasks />
                        <p>No tasks assigned to this milestone</p>
                      </div>
                    ) : (
                      <div className="ptm-items-list">
                        {tasks.map((task) => {
                          // IMPORTANT: Convert task status GUID to name using getTaskStatusName
                          let taskStatusName = "Not Set";

                          if (getTaskStatusName) {
                            // If we have the helper function, use it
                            taskStatusName = getTaskStatusName(task.taskStatus);
                          } else {
                            // Fallback: check if it's a GUID or already a name
                            const isGuid =
                              task.taskStatus &&
                              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                                task.taskStatus,
                              );
                            if (isGuid) {
                              taskStatusName =
                                task.taskStatus.substring(0, 8) + "...";
                            } else {
                              taskStatusName = task.taskStatus || "Not Set";
                            }
                          }

                          const assignedUser =
                            taskAssignedUsers?.[parseInt(task.assignedTo)] ||
                            task.assignedTo ||
                            "Unassigned";

                          const taskStatusClass =
                            getTaskStatusClass(taskStatusName);
                          const priorityClass = getPriorityClass(
                            task.taskPriority,
                          );

                          return (
                            <div key={task.taskId} className="ptm-item-row">
                              <div className="ptm-item-info">
                                <div className="ptm-item-id">
                                  #{task.taskId}
                                </div>
                                <div
                                  className="ptm-item-name clickable"
                                  onClick={() =>
                                    handleTaskNameClick(task.taskId)
                                  }
                                  title="Click to view task details"
                                >
                                  {task.taskName}
                                </div>
                              </div>
                              <div className="ptm-item-details">
                                <span className="ptm-item-assignee">
                                  <FaUser /> {assignedUser}
                                </span>
                                <span
                                  className={`ptm-task-status ${taskStatusClass}`}
                                >
                                  {taskStatusName}
                                </span>
                                <span
                                  className={`ptm-task-priority ${priorityClass}`}
                                >
                                  <FaFlag className="ptm-priority-icon" />
                                  {task.taskPriority || "Not Set"}
                                </span>
                                {task.taskEndDate && (
                                  <span className="ptm-item-date">
                                    <FaCalendarAlt /> Due:{" "}
                                    {formatDate(task.taskEndDate)}
                                  </span>
                                )}
                              </div>
                              {canEdit && (
                                <div className="ptm-item-actions">
                                  <button
                                    className="ptm-action-btn-small edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditTask(task.taskId);
                                    }}
                                    title="Edit task"
                                  >
                                    <FaEdit /> Edit
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Processing overlay */}
      {processing && (
        <div className="ptm-processing-overlay">
          <div className="ptm-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ProjectMilestoneTab;

// import React, { useState, useEffect } from "react";
// import { Button, Modal, Form, Badge } from "react-bootstrap";
// import moment from "moment";
// import {
//   FaEdit,
//   FaTrash,
//   FaTrashRestore,
//   FaTrashAlt,
//   FaFlag,
//   FaTasks,
//   FaCalendarAlt,
//   FaUser,
//   FaPlus,
//   FaChevronDown,
//   FaChevronUp,
//   FaPercentage,
//   FaInfoCircle,
// } from "react-icons/fa";
// import { MdOutlineTrackChanges } from "react-icons/md";
// import { TfiViewListAlt } from "react-icons/tfi";
// import { toast } from "react-toastify";
// import "./ProjectMilestoneTab.css";

// const ProjectMilestoneTab = ({
//   // Data
//   milestones,
//   milestonesLoading,
//   milestoneTasks,
//   expandedMilestones,
//   taskAssignedUsers,

//   // Permissions
//   hasProjectAdminRole,
//   hasProjectManagerRole,
//   hasEditPermission,

//   // Handlers
//   handleCreateMilestone,
//   handleEditMilestone,
//   toggleMilestoneExpand,
//   handleTaskNameClick,
//   handleEditTask,
//   formatDate,
//   getStatusBadge,
//   getPriorityBadge,
//   refreshMilestones,

//   // Delete/Restore handlers
//   onSoftDeleteMilestone,
//   onRestoreMilestone,
//   onHardDeleteMilestone,

//   showInactiveMilestones,
//   setShowInactiveMilestones,

//   // IMPORTANT: Add these props for task status resolution
//   getTaskStatusName, // Function to convert task status GUID to name
//   milestoneTypesMap = {},
//   milestoneStatusesMap = {},
// }) => {
//   const canEdit = hasProjectAdminRole() || hasProjectManagerRole();

//   // Local state for delete/restore operations
//   const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
//   const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
//   const [selectedMilestone, setSelectedMilestone] = useState(null);
//   const [deleteType, setDeleteType] = useState(null);
//   const [processing, setProcessing] = useState(false);

//   // Helper function to resolve type GUID to name
//   const getTypeDisplayName = (typeGuid) => {
//     if (!typeGuid) return "Not set";
//     if (typeof typeGuid === "string" && !typeGuid.includes("-")) {
//       return typeGuid;
//     }
//     const typeGuidLower = typeGuid.toLowerCase();
//     return milestoneTypesMap[typeGuidLower] || typeGuid.substring(0, 8);
//   };

//   // Helper function to resolve status GUID to name
//   const getStatusDisplayName = (statusGuid) => {
//     if (!statusGuid) return "Not Started";
//     if (typeof statusGuid === "string" && !statusGuid.includes("-")) {
//       return statusGuid;
//     }
//     const statusGuidLower = statusGuid.toLowerCase();
//     return milestoneStatusesMap[statusGuidLower] || statusGuid.substring(0, 8);
//   };

//   // Helper function to get milestone status badge class
//   const getMilestoneStatusBadgeClass = (statusName) => {
//     const statusMap = {
//       Completed: "ptm-status-completed",
//       "In Progress": "ptm-status-progress",
//       "On Hold": "ptm-status-hold",
//       Cancelled: "ptm-status-cancelled",
//       Delayed: "ptm-status-delayed",
//       "Not Started": "ptm-status-notstarted",
//     };
//     return statusMap[statusName] || "ptm-status-default";
//   };

//   // Helper function to get task status class based on status name
//   const getTaskStatusClass = (statusName) => {
//     const statusClassMap = {
//       Completed: "ptm-task-completed",
//       "In Progress": "ptm-task-progress",
//       Open: "ptm-task-open",
//       Closed: "ptm-task-closed",
//       Pending: "ptm-task-pending",
//       Approved: "ptm-task-approved",
//       Rejected: "ptm-task-rejected",
//       "Not Started": "ptm-task-notstarted",
//       "On Hold": "ptm-task-hold",
//       Cancelled: "ptm-task-cancelled",
//     };
//     return statusClassMap[statusName] || "ptm-task-default";
//   };

//   // Helper function to get priority class
//   const getPriorityClass = (priority) => {
//     if (!priority) return "ptm-priority-default";
//     const priorityLower = priority.toLowerCase();
//     if (priorityLower === "high" || priorityLower === "critical")
//       return "ptm-priority-high";
//     if (priorityLower === "medium") return "ptm-priority-medium";
//     if (priorityLower === "low") return "ptm-priority-low";
//     return "ptm-priority-default";
//   };

//   // Calculate statistics
//   const activeMilestonesCount = milestones.filter(
//     (m) => m.isActive !== false && m.active !== false,
//   ).length;
//   const inactiveMilestonesCount = milestones.filter(
//     (m) => m.isActive === false || m.active === false,
//   ).length;

//   const getTotalProgress = () => {
//     const activeMilestones = milestones.filter(
//       (m) => m.isActive !== false && m.active !== false,
//     );
//     if (activeMilestones.length === 0) return 0;
//     const totalProgress = activeMilestones.reduce((sum, milestone) => {
//       return (
//         sum + (milestone.calculatedPercentage || milestone.percentage || 0)
//       );
//     }, 0);
//     return Math.round(totalProgress / activeMilestones.length);
//   };

//   const getProgressColor = (percent) => {
//     if (percent >= 80) return "#10b981";
//     if (percent >= 50) return "#f59e0b";
//     if (percent >= 25) return "#f97316";
//     return "#ef4444";
//   };

//   // Soft delete handler
//   const handleSoftDelete = async (milestone) => {
//     if (!onSoftDeleteMilestone) {
//       toast.error("Soft delete function not available");
//       return;
//     }

//     if (
//       !window.confirm(
//         `Are you sure you want to deactivate "${milestone.name}"?\n\nDeactivated milestones will be hidden but can be restored later.`,
//       )
//     ) {
//       return;
//     }

//     setProcessing(true);
//     try {
//       const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//       const result = await onSoftDeleteMilestone(
//         milestone.milestoneMavroId,
//         String(userDetails?.userId),
//       );

//       if (result?.message || result?.Message) {
//         toast.success(
//           result.message ||
//             result.Message ||
//             "Milestone deactivated successfully",
//         );
//       } else {
//         toast.success("Milestone deactivated successfully");
//       }

//       if (refreshMilestones) refreshMilestones();
//     } catch (error) {
//       console.error("Error deactivating milestone:", error);
//       toast.error(error.message || "Failed to deactivate milestone");
//     } finally {
//       setProcessing(false);
//     }
//   };

//   // Restore handler
//   const handleRestore = async (milestone) => {
//     if (!onRestoreMilestone) {
//       toast.error("Restore function not available");
//       return;
//     }

//     if (
//       !window.confirm(`Are you sure you want to restore "${milestone.name}"?`)
//     ) {
//       return;
//     }

//     setProcessing(true);
//     try {
//       const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//       const result = await onRestoreMilestone(
//         milestone.milestoneMavroId,
//         String(userDetails?.userId),
//       );

//       if (result?.message || result?.Message) {
//         toast.success(
//           result.message || result.Message || "Milestone restored successfully",
//         );
//       } else {
//         toast.success("Milestone restored successfully");
//       }

//       if (refreshMilestones) refreshMilestones();
//     } catch (error) {
//       console.error("Error restoring milestone:", error);
//       toast.error(error.message || "Failed to restore milestone");
//     } finally {
//       setProcessing(false);
//     }
//   };

//   // Permanent delete handler
//   const handlePermanentDelete = async (milestone) => {
//     if (!onHardDeleteMilestone) {
//       toast.error("Permanent delete function not available");
//       return;
//     }

//     if (
//       !window.confirm(
//         `⚠️ WARNING: This action is irreversible!\n\nAre you sure you want to permanently delete "${milestone.name}"?\n\nThis will permanently remove the milestone and cannot be undone.`,
//       )
//     ) {
//       return;
//     }

//     setProcessing(true);
//     try {
//       const result = await onHardDeleteMilestone(milestone.milestoneMavroId);

//       if (result?.message || result?.Message) {
//         toast.warning(
//           result.message || result.Message || "Milestone permanently deleted",
//         );
//       } else {
//         toast.warning("Milestone permanently deleted");
//       }

//       if (refreshMilestones) refreshMilestones();
//     } catch (error) {
//       console.error("Error deleting milestone:", error);
//       toast.error(error.message || "Failed to delete milestone");
//     } finally {
//       setProcessing(false);
//     }
//   };

//   // Filter milestones based on showInactiveMilestones
//   const displayedMilestones = showInactiveMilestones
//     ? milestones
//     : milestones.filter((m) => m.isActive !== false && m.active !== false);

//   return (
//     <div className="ptm-container">
//       {/* Header Section */}
//       <div className="ptm-header">
//         <div className="ptm-header-left">
//           <div className="ptm-stats">
//             <div className="ptm-stat-item">
//               <span className="ptm-stat-value">{milestones.length}</span>
//               <span className="ptm-stat-label">Total Milestones</span>
//             </div>
//             <div className="ptm-stat-divider"></div>
//             <div className="ptm-stat-item">
//               <span className="ptm-stat-value ptm-active">
//                 {activeMilestonesCount}
//               </span>
//               <span className="ptm-stat-label">Active</span>
//             </div>
//             <div className="ptm-stat-divider"></div>
//             <div className="ptm-stat-item">
//               <span className="ptm-stat-value ptm-inactive">
//                 {inactiveMilestonesCount}
//               </span>
//               <span className="ptm-stat-label">Inactive</span>
//             </div>
//           </div>
//           <div className="ptm-progress-ring">
//             <svg width="50" height="50" viewBox="0 0 50 50">
//               <circle
//                 cx="25"
//                 cy="25"
//                 r="22"
//                 fill="none"
//                 stroke="#e2e8f0"
//                 strokeWidth="3"
//               />
//               <circle
//                 cx="25"
//                 cy="25"
//                 r="22"
//                 fill="none"
//                 stroke={getProgressColor(getTotalProgress())}
//                 strokeWidth="3"
//                 strokeDasharray={`${getTotalProgress() * 1.38} 138`}
//                 strokeLinecap="round"
//                 transform="rotate(-90 25 25)"
//               />
//             </svg>
//             <span className="ptm-progress-text">{getTotalProgress()}%</span>
//           </div>
//         </div>

//         <div className="ptm-header-right">
//           <div className="ptm-inactive-switch">
//             <Form.Check
//               type="switch"
//               id="show-inactive-milestones-switch"
//               label="Show Inactive"
//               checked={showInactiveMilestones}
//               onChange={(e) => setShowInactiveMilestones(e.target.checked)}
//               className="ptm-switch"
//             />
//           </div>

//           {canEdit && !showInactiveMilestones && (
//             <button
//               className="ptm-btn-primary"
//               onClick={handleCreateMilestone}
//               disabled={processing}
//             >
//               <FaPlus /> Add Milestone
//             </button>
//           )}
//         </div>
//       </div>

//       {/* Content Section */}
//       {milestonesLoading ? (
//         <div className="ptm-loading">
//           <div className="ptm-spinner"></div>
//           <p>Loading milestones...</p>
//         </div>
//       ) : displayedMilestones.length === 0 ? (
//         <div className="ptm-empty">
//           <div className="ptm-empty-icon">
//             <FaFlag />
//           </div>
//           <h3>No Milestones Found</h3>
//           <p>
//             {showInactiveMilestones
//               ? "No inactive milestones found."
//               : "Create your first milestone to track project progress."}
//           </p>
//           {canEdit && !showInactiveMilestones && (
//             <button className="ptm-btn-primary" onClick={handleCreateMilestone}>
//               <FaPlus /> Create Milestone
//             </button>
//           )}
//         </div>
//       ) : (
//         <div className="ptm-grid">
//           {displayedMilestones.map((milestone) => {
//             const isActive =
//               milestone.isActive !== false && milestone.active !== false;
//             const progress =
//               milestone.calculatedPercentage || milestone.percentage || 0;
//             const isExpanded =
//               expandedMilestones[milestone.milestoneMavroId] || false;
//             const tasks = milestoneTasks[milestone.milestoneMavroId] || [];

//             const typeDisplayName = getTypeDisplayName(milestone.Type);
//             const statusDisplayName = getStatusDisplayName(milestone.Status);
//             const statusBadgeClass =
//               getMilestoneStatusBadgeClass(statusDisplayName);

//             return (
//               <div
//                 key={milestone.milestoneMavroId}
//                 className={`ptm-card ${!isActive ? "inactive" : ""} ${isExpanded ? "expanded" : ""}`}
//               >
//                 {/* Card Header */}
//                 <div
//                   className="ptm-card-header"
//                   onClick={() =>
//                     toggleMilestoneExpand(milestone.milestoneMavroId)
//                   }
//                 >
//                   <div className="ptm-card-header-left">
//                     <div className="ptm-card-icon">
//                       <FaFlag />
//                     </div>
//                     <div className="ptm-card-info">
//                       <div className="ptm-card-title">
//                         <span className="ptm-card-name">{milestone.name}</span>
//                         {!isActive && (
//                           <Badge bg="secondary" className="ptm-inactive-badge">
//                             Inactive
//                           </Badge>
//                         )}
//                       </div>
//                       <div className="ptm-card-meta">
//                         <span className="ptm-card-id">
//                           {milestone.milestoneMavroId}
//                         </span>
//                         {typeDisplayName && typeDisplayName !== "Not set" && (
//                           <span className="ptm-card-type">
//                             {typeDisplayName}
//                           </span>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                   <div className="ptm-card-header-right">
//                     <div className="ptm-actions">
//                       <button
//                         className="ptm-action-btn view"
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           if (!isExpanded)
//                             toggleMilestoneExpand(milestone.milestoneMavroId);
//                         }}
//                         title="View tasks"
//                       >
//                         <TfiViewListAlt /> Tasks ({tasks.length})
//                       </button>

//                       {isActive
//                         ? canEdit && (
//                             <>
//                               <button
//                                 className="ptm-action-btn edit"
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   handleEditMilestone(milestone);
//                                 }}
//                                 title="Edit milestone"
//                               >
//                                 <FaEdit /> Edit
//                               </button>
//                               <button
//                                 className="ptm-action-btn deactivate"
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   handleSoftDelete(milestone);
//                                 }}
//                                 title="Deactivate milestone (soft delete)"
//                                 disabled={processing}
//                               >
//                                 <FaTrash /> Deactivate
//                               </button>
//                             </>
//                           )
//                         : canEdit && (
//                             <>
//                               <button
//                                 className="ptm-action-btn restore"
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   handleRestore(milestone);
//                                 }}
//                                 title="Restore milestone"
//                                 disabled={processing}
//                               >
//                                 <FaTrashRestore /> Restore
//                               </button>
//                               <button
//                                 className="ptm-action-btn hard-delete"
//                                 onClick={(e) => {
//                                   e.stopPropagation();
//                                   handlePermanentDelete(milestone);
//                                 }}
//                                 title="Permanently delete milestone"
//                                 disabled={processing}
//                               >
//                                 <FaTrashAlt /> Delete
//                               </button>
//                             </>
//                           )}
//                     </div>

//                     <div className="ptm-card-stats">
//                       <div className="ptm-stat-chip">
//                         <FaTasks />
//                         <span>{tasks.length} tasks</span>
//                       </div>
//                       {isActive && (
//                         <div className="ptm-progress-mini">
//                           <div className="ptm-progress-bar-mini">
//                             <div
//                               className="ptm-progress-fill-mini"
//                               style={{
//                                 width: `${progress}%`,
//                                 backgroundColor: getProgressColor(progress),
//                               }}
//                             ></div>
//                           </div>
//                           <span className="ptm-progress-text-mini">
//                             {progress}%
//                           </span>
//                         </div>
//                       )}
//                     </div>
//                     <div className="ptm-card-expand">
//                       {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
//                     </div>
//                   </div>
//                 </div>

//                 {/* Card Body */}
//                 {(isActive || isExpanded) && (
//                   <div className="ptm-card-body">
//                     <div className="ptm-status-section">
//                       <span
//                         className={`ptm-milestone-status ${statusBadgeClass}`}
//                       >
//                         {statusDisplayName || "Not Started"}
//                       </span>
//                       {milestone.createdBy &&
//                         !milestone.createdBy.includes("-") && (
//                           <div className="ptm-created-info">
//                             <FaUser />
//                             <span>Created by: {milestone.createdBy}</span>
//                             {milestone.createdOnDate && (
//                               <span className="ptm-created-date">
//                                 on {formatDate(milestone.createdOnDate)}
//                               </span>
//                             )}
//                           </div>
//                         )}
//                     </div>

//                     {milestone.description && (
//                       <div className="ptm-description">
//                         <FaInfoCircle />
//                         <span>{milestone.description}</span>
//                       </div>
//                     )}

//                     <div className="ptm-dates-section">
//                       <div className="ptm-date-group">
//                         <div className="ptm-date-label">Planned Dates</div>
//                         <div className="ptm-dates">
//                           <span>
//                             <FaCalendarAlt /> Start:{" "}
//                             {formatDate(milestone.plannedStartDate)}
//                           </span>
//                           <span>
//                             <FaCalendarAlt /> End:{" "}
//                             {formatDate(milestone.plannedEndDate)}
//                           </span>
//                         </div>
//                       </div>
//                       {(milestone.actualStartDate ||
//                         milestone.actualEndDate) && (
//                         <div className="ptm-date-group">
//                           <div className="ptm-date-label">Actual Dates</div>
//                           <div className="ptm-dates">
//                             <span>
//                               <FaCalendarAlt /> Start:{" "}
//                               {formatDate(milestone.actualStartDate) || "-"}
//                             </span>
//                             <span>
//                               <FaCalendarAlt /> End:{" "}
//                               {formatDate(milestone.actualEndDate) || "-"}
//                             </span>
//                           </div>
//                         </div>
//                       )}
//                     </div>

//                     {milestone.calculatedPercentage !== undefined &&
//                       isActive && (
//                         <div className="ptm-progress-details">
//                           <div className="ptm-progress-stat">
//                             <FaPercentage />
//                             <span>
//                               Completed: {milestone.completedTasks || 0}/
//                               {milestone.totalTasks || 0} tasks
//                             </span>
//                           </div>
//                         </div>
//                       )}
//                   </div>
//                 )}

//                 {/* Expanded View - Tasks List with proper status names */}
//                 {isExpanded && (
//                   <div className="ptm-expanded-view">
//                     <div className="ptm-expanded-header">
//                       <h4>
//                         <FaTasks />
//                         Tasks in this milestone
//                       </h4>
//                       <span className="ptm-expanded-count">
//                         Total: {tasks.length}
//                       </span>
//                     </div>

//                     {tasks.length === 0 ? (
//                       <div className="ptm-items-empty">
//                         <FaTasks />
//                         <p>No tasks assigned to this milestone</p>
//                       </div>
//                     ) : (
//                       <div className="ptm-items-list">
//                         {tasks.map((task) => {
//                           // IMPORTANT: Convert task status GUID to name using getTaskStatusName
//                           let taskStatusName = "Not Set";

//                           if (getTaskStatusName) {
//                             // If we have the helper function, use it
//                             taskStatusName = getTaskStatusName(task.taskStatus);
//                           } else {
//                             // Fallback: check if it's a GUID or already a name
//                             const isGuid =
//                               task.taskStatus &&
//                               /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
//                                 task.taskStatus,
//                               );
//                             if (isGuid) {
//                               taskStatusName =
//                                 task.taskStatus.substring(0, 8) + "...";
//                             } else {
//                               taskStatusName = task.taskStatus || "Not Set";
//                             }
//                           }

//                           const assignedUser =
//                             taskAssignedUsers?.[parseInt(task.assignedTo)] ||
//                             task.assignedTo ||
//                             "Unassigned";

//                           const taskStatusClass =
//                             getTaskStatusClass(taskStatusName);
//                           const priorityClass = getPriorityClass(
//                             task.taskPriority,
//                           );

//                           return (
//                             <div key={task.taskId} className="ptm-item-row">
//                               <div className="ptm-item-info">
//                                 <div className="ptm-item-id">
//                                   #{task.taskId}
//                                 </div>
//                                 <div
//                                   className="ptm-item-name clickable"
//                                   onClick={() =>
//                                     handleTaskNameClick(task.taskId)
//                                   }
//                                   title="Click to view task details"
//                                 >
//                                   {task.taskName}
//                                 </div>
//                               </div>
//                               <div className="ptm-item-details">
//                                 <span className="ptm-item-assignee">
//                                   <FaUser /> {assignedUser}
//                                 </span>
//                                 <span
//                                   className={`ptm-task-status ${taskStatusClass}`}
//                                 >
//                                   {taskStatusName}
//                                 </span>
//                                 <span
//                                   className={`ptm-task-priority ${priorityClass}`}
//                                 >
//                                   <FaFlag className="ptm-priority-icon" />
//                                   {task.taskPriority || "Not Set"}
//                                 </span>
//                                 {task.taskEndDate && (
//                                   <span className="ptm-item-date">
//                                     <FaCalendarAlt /> Due:{" "}
//                                     {formatDate(task.taskEndDate)}
//                                   </span>
//                                 )}
//                               </div>
//                               {canEdit && (
//                                 <div className="ptm-item-actions">
//                                   <button
//                                     className="ptm-action-btn-small edit"
//                                     onClick={(e) => {
//                                       e.stopPropagation();
//                                       handleEditTask(task.taskId);
//                                     }}
//                                     title="Edit task"
//                                   >
//                                     <FaEdit /> Edit
//                                   </button>
//                                 </div>
//                               )}
//                             </div>
//                           );
//                         })}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </div>
//             );
//           })}
//         </div>
//       )}

//       {/* Processing overlay */}
//       {processing && (
//         <div className="ptm-processing-overlay">
//           <div className="ptm-spinner"></div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ProjectMilestoneTab;
